<script setup lang="ts">
import {computed, CSSProperties, PropType, provide, ref, Ref, toValue, useTemplateRef, watch} from "vue";
import {QExpansionItem} from "quasar";
import Translator from "@/utils/common/Translator.ts";
import EditScreenGroup from "@/components/EditView/editScreens/EditScreenGroup.vue";
import EditScreenGrid from "@/components/EditView/editScreens/EditScreenGrid.vue";
import EditScreenActionsBlock from "@/components/EditView/editScreens/EditScreenActionsBlock.vue";
import EditScreenDashBoard from "@/components/EditView/editScreens/EditScreenDashBoard.vue";
import EditScreenList from "@/components/EditView/editScreens/EditScreenList.vue";
import EditScreenReport from "@/components/EditView/editScreens/EditScreenReport.vue";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {widgetPageIndexKey, WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import {NamaNavigationArgs, NamaVueWidget} from "@/utils/common/NamaVueWidget.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import CriteriaEditorWidget from "@/components/EditView/widgets/CriteriaEditorWidget.vue";
import DiscussionEditorWidget from "@/components/EditView/widgets/DiscussionEditorWidget.vue";
import {useEventListener} from "@vueuse/core";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import GridSize from "@/utils/constants/GridSize.ts";
import EditScreenEmbeddedEdit from "@/components/EditView/editScreens/EditScreenEmbeddedEdit.vue";
import EditUiElementPropertiesBtn from "@/components/EditView/EditUIElementPropertiesBtn.vue";
import ApprovalCaseTimeline from "@/components/EditView/editScreens/ApprovalCaseTimeline.vue";
import ApprovalCaseBadges from "@/components/EditView/editScreens/ApprovalCaseBadges.vue";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import NamaResizer from "@/components/NamaResizer.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import EditScreenGridToolbar from "@/components/EditView/EditScreenGridToolbar.vue";
import EditScreenTechnicianAppointmentCreator from "@/components/EditView/editScreens/EditScreenTechnicianAppointmentCreator.vue";
import ConstantViewNames from "@/utils/constants/ConstantViewNames.ts";
import ListScreenLayout = namaTypes.ListScreenLayout;

let props = defineProps({
  block: {
    type: Object as PropType<namaTypes.EditScreenBlock | namaTypes.EditScreenActionsBlock | namaTypes.EditScreenDashBoard | namaTypes.EditScreenField | namaTypes.EditScreenGrid | namaTypes.EditScreenGroup | namaTypes.EditScreenLayout | namaTypes.EditScreenList>,
    required: true
  },
  presenter: {type: Object as PropType<WidgetsPresenter>, required: true},
  blockIndex: {type: Number, required: true},
  pageIndex: {type: Number, required: true},
  showMainFile: {type: Boolean, required: true},
});
const emit = defineEmits<{
  (e: 'navigateToNext', args: NamaNavigationArgs): void;
  (e: 'discussionAdded', args: NamaVueWidget): void;
}>();
provide(widgetPageIndexKey, props.pageIndex)
const {compactMode, isResizing, fieldToFocusOptions, showIdsActive} = storeToRefs(useNamaFlagsStore());
const presenter: EditPresenter = props.presenter as EditPresenter;
const block: (namaTypes.CrossTableBlock & namaTypes.EditScreenActionsBlock & namaTypes.EditScreenBlock & namaTypes.EditScreenDashBoard & namaTypes.EditScreenGrid & namaTypes.EditScreenGroup & namaTypes.EditScreenList & namaTypes.EditScreenReport) = props.block;
const blockId = block.title ? block.title.resourceId : 'p' + props.pageIndex + 'g' + props.blockIndex;
const blockKey = presenter.entityType + '_' + blockId;
const blockHeightKey = blockKey + "_height"
const expansionItem: Ref<QExpansionItem | undefined> = ref();
const actionsWidgetExpanded = ref(localStorage.getItem(blockKey) ? localStorage.getItem(blockKey) === 'true' : true);
const isExpanded = ref(!isDefaultCollapsed());
const {setResizerPosition} = useNamaFlagsStore()
const blockRef = ref<InstanceType<typeof EditScreenEmbeddedEdit> & InstanceType<typeof EditScreenGrid> & NamaVueWidget>();
const gridToolbarRef = ref<InstanceType<typeof EditScreenGridToolbar>>();
const blockElement = useTemplateRef<HTMLDivElement | null>("blockElement")
const resizerTrigger = useTemplateRef<HTMLDivElement | null>("resizerTrigger")
const editViewListLayout = ref<ListScreenLayout>()
const blockHeight = ref<CSSProperties['height']>(calcBlockHeight())
const blockTitle = computed(() => {
  if (block['namaClassTypeId'] === 'EditScreenActionsBlock') {
    if (block.title)
      return Translator.translateTitledIDWithPrefixes(block, presenter.entityType)
    return Translator.translateId('actionButtons');
  } else if (block['namaClassTypeId'] === 'EditScreenEmbeddedEdit') {
    let documentTitle: string = blockRef.value?.title ?? '';
    if (!documentTitle || !isExpanded.value)
      documentTitle = Translator.translateId(block['entityType']);
    return Translator.translateTitledIDWithPrefixes(block, presenter.entityType) + ' / ' + documentTitle;
  } else if (block["namaClassTypeId"] === "EditScreenList" && showIdsActive.value) {
    let title = "Owner entity type : " + block.listViewEntityType + " , List View Id : " + block.listViewId + " , Question Field Id : "
      + block.linkFieldId;
    const listLayout = editViewListLayout.value
    if (!listLayout)
      return title;
    const queriedClass = listLayout.queriedClass;
    if (ObjectChecker.isNotEmptyOrNull(queriedClass))
      title += ", Class: " + StringUtils.substringAfterLast(queriedClass, ".")
    if (ObjectChecker.isNotEmptyOrNull(listLayout.objectName) && ObjectChecker.isNotEmptyOrNull(StringUtils.substringAfterLast(listLayout.objectName, ".").trim()))
      title += ", Object Name: " + listLayout.objectName;
    return title;
  } else
    return Translator.translateTitledIDWithPrefixes(block, presenter.entityType);
})

const editScreenBlocks = {
  EditScreenGroup: {component: EditScreenGroup, defaultIconCode: "nm-group-block"},
  EditScreenGrid: {component: EditScreenGrid, defaultIconCode: "nm-grid-block"},
  EditScreenDashBoard: {component: EditScreenDashBoard, defaultIconCode: "nm-dashboards"},
  EditScreenList: {component: EditScreenList, defaultIconCode: "nm-list-block"},
  EditScreenReport: {component: EditScreenReport, defaultIconCode: "nm-report"},
  DiscussionEditorWidget: {component: DiscussionEditorWidget},
  CriteriaEditorWidget: {component: CriteriaEditorWidget},
  EditScreenEmbeddedEdit: {component: EditScreenEmbeddedEdit, defaultIconCode: "nm-monitoring"},
  EditScreenTimeline: {component: ApprovalCaseTimeline, defaultIconCode: "nm-timeline"},
  EditScreenBadges: {component: ApprovalCaseBadges, defaultIconCode: "nm-group-block"}
}

const blockDefinition = computed(() => {
  if (block['namaClassTypeId'] === 'EditScreenEmbeddedEdit' && block['viewName'] === ConstantViewNames.TECHNICIAN_APPOINTMENT_CREATOR)
    return {component: EditScreenTechnicianAppointmentCreator, defaultIconCode: "event"};
  return editScreenBlocks[block['namaClassTypeId'] as keyof typeof editScreenBlocks];
});

function onStopResize(height: CSSProperties['height']) {
  NamaCommon.setInStorage(blockHeightKey, height);
}

function defaultBlockHeight(): CSSProperties['height'] {
  if (!isResizable())
    return 'auto'
  else
    return GUIUtilities.gridSizeToPixels(ObjectChecker.getFirstNotEmptyObj(block.gridSize, block.size, GridSize.Medium))
}

function calcBlockHeight(): CSSProperties['height'] {
  if (!isResizable())
    return defaultBlockHeight();
  const storedValue = localStorage.getItem(blockHeightKey);
  const parsedAsNumber = parseInt(storedValue ?? '');
  if (ObjectChecker.isEmptyOrNull(storedValue))
    return defaultBlockHeight();
  else if (storedValue === "auto" || isNaN(parsedAsNumber))
    return "auto";
  else
    return parsedAsNumber;
}

function handleShow() {
  isExpanded.value = true;
  localStorage.setItem(blockKey, 'true');
}

function handleHide() {
  isExpanded.value = false;
  localStorage.setItem(blockKey, 'false');
}

function onListScreenLayoutFetched(layout: ListScreenLayout) {
  editViewListLayout.value = layout;
}

function onExpand() {
  actionsWidgetExpanded.value = !actionsWidgetExpanded.value;
  setTimeout(() => {
    isExpanded.value = !isExpanded.value;
    localStorage.setItem(blockKey, isExpanded.value + '');
  }, 150);
}

function toggle() {
  isExpanded.value = !isExpanded.value;
  localStorage.setItem(blockKey, isExpanded.value + '');
  setTimeout(() => {
    actionsWidgetExpanded.value = !actionsWidgetExpanded.value;
  }, 150);
}

function isDefaultCollapsed(): boolean {
  const namaClassTypeIds = ['EditScreenReport', 'EditScreenList', 'EditScreenBlock', 'EditScreenDashBoard', 'EditScreenEmbeddedEdit'];
  if (block.openByDefault)
    return false;
  else if (ObjectChecker.isAnyEqualToFirst(block['namaClassTypeId'], ...namaClassTypeIds))
    return true;
  else
    return localStorage.getItem(blockKey) === 'false';
}

function focus(args: NamaNavigationArgs) {
  if (!blockRef.value)
    return;
  if (!blockRef.value.focus)
    return;
  blockRef.value.focus(args);
}

function isResizable(): boolean {
  return block['namaClassTypeId'] !== 'EditScreenGroup' && block['namaClassTypeId'] !== 'EditScreenActionsBlock' && block['namaClassTypeId'] !== 'DiscussionEditorWidget'
}

function canReceiveFocus(): boolean {
  if (!isExpanded.value)
    return false;
  if (!blockRef.value)
    return false;
  if (!blockRef.value.canReceiveFocus)
    return false;
  return blockRef.value.canReceiveFocus();
}

function fetchBlockWidget() {
  return blockRef.value;
}

function startResize(e: MouseEvent | TouchEvent) {
  let clientY: number;
  if (e instanceof TouchEvent) {
    const touch = e.touches[0] || e.changedTouches[0]
    clientY = touch.clientY
  } else {
    clientY = e.clientY;
  }
  setResizerPosition(clientY)
}

function stopResize(e: MouseEvent | TouchEvent) {
  const el = blockElement.value
  if (!el)
    return;
  let clientY: number;
  if (e instanceof TouchEvent) {
    const touch = e.touches[0] || e.changedTouches[0]
    clientY = touch.clientY
  } else {
    clientY = e.clientY;
  }
  isResizing.value = false;
  blockHeight.value = (clientY - blockElement.value.getBoundingClientRect().top) + 'px'
  setTimeout(() => {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  })
  document.removeEventListener('mousemove', startResize);
  document.removeEventListener('mouseup', stopResize);
  document.removeEventListener('touchmove', startResize)
  document.removeEventListener('touchend', stopResize)
}

function setInitialPositionForResizerLine() {
  const value = resizerTrigger.value;
  if (!value)
    return;
  const bounding = value.getBoundingClientRect();
  setResizerPosition(bounding.top + bounding.height / 2);
}

useEventListener(resizerTrigger, "mousedown", (e: MouseEvent) => {
  isResizing.value = true;
  setInitialPositionForResizerLine();
  e.preventDefault()
  document.addEventListener('mousemove', startResize);
  document.addEventListener('mouseup', stopResize);
})
useEventListener(resizerTrigger, "touchstart", (e: TouchEvent) => {
  const touch = e.touches[0] || e.changedTouches[0]
  const touchedEl = document.elementFromPoint(touch.clientX, touch.clientY);
  const resizer = resizerTrigger.value;
  if (!resizer || !touch || !resizer.contains(touchedEl))
    return;
  setInitialPositionForResizerLine();
  isResizing.value = true;
  e.preventDefault()
  document.addEventListener('touchmove', startResize);
  document.addEventListener('touchend', stopResize);
})

watch(blockHeight, (value) => {
  localStorage.setItem(blockHeightKey, (value ?? '').toString());
})
watch(() => fieldToFocusOptions.value.field, (searchItem) => {
  if (!searchItem)
    return;
  if (searchItem?.record?.extraData?.block !== block)
    return;
  if (toValue(isExpanded))
    return;
  handleShow()
}, {immediate: true})

watch(isResizing, (value) => {
  if (value)
    document.body.classList.add('resize-ptr');
  else
    document.body.classList.remove('resize-ptr');
})
defineExpose({canReceiveFocus, focus, fetchBlockWidget});
</script>

<template>
  <div v-if="block['namaClassTypeId'] == 'EditScreenActionsBlock'"
       class="q-expansion-item"
       :data-entity-type="presenter.entityType"
       :id="blockId"
       :class="[actionsWidgetExpanded?'tw-w-full':'tw-grow tw-max-w-full tw-min-w-[20%] tw-w-full tw-basis-1/5 mobile:tw-basis-full',block['namaClassTypeId']]">
    <q-expansion-item
      v-if="!isExpanded"
      :model-value="actionsWidgetExpanded"
      @update:model-value="onExpand"
      class="overflow-hidden tw-w-full"
      :label="blockTitle"
      :id="blockId"
      :dense="compactMode"
      :header-class="[isExpanded?'tw-bg-namaBackground-deep-light tw-pr-0 dark:tw-bg-namaBackground-deep-dark dark:tw-text-white tw-text-base tw-rounded-lg':'tw-bg-namaBackground-dim-light tw-pr-0 dark:tw-bg-namaBackground-dim-dark dark:tw-text-white tw-text-base tw-rounded-lg']"
      expand-icon-class="dark:tw-text-white">
      <template #header>
        <div class="tw-flex tw-gap-3 tw-items-center tw-w-[85%] tw-max-w-[85%]  tw-pr-3"
             :title="blockTitle">
          <NamaIcon class="tw-size-5 compact:tw-size-[18px] ultra:tw-size-[15px]" :block="block" :presenter="presenter" default-icon="nm-actions"/>
          <div
            class="tw-inline-flex tw-items-center tw-gap-1 tw-flex-grow tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-line-clamp-1 tw-font-bold tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark">
            <span v-text="blockTitle"/>
            <EditUiElementPropertiesBtn :blockOrField="block" :entity-type="presenter.entityType"/>
          </div>
        </div>
        <q-space/>
      </template>
    </q-expansion-item>
    <q-card
      v-else
      class="dark:tw-text-white tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-cursor-pointer dark:hover:tw-bg-[#4B4B4B] hover:tw-bg-[#E7E7E7]"
      @click="toggle">
      <q-card-section class="tw-px-3 tw-py-2 compact:tw-py-1 ultra:tw-py-0.5">
        <EditScreenActionsBlock :block="block" :blockIndex="blockIndex" :presenter="presenter" :pageIndex="pageIndex" @toggle="toggle"/>
      </q-card-section>
    </q-card>
  </div>
  <div v-else :data-entity-type="presenter.entityType" class="tw-bg-transparent tw-rounded-lg tw-group"
       :class="[isExpanded?'tw-w-full':'tw-grow tw-max-w-full tw-min-w-[20%] tw-w-full tw-basis-1/5 mobile:tw-basis-full']">
    <q-expansion-item
      ref="expansionItem"
      :model-value="isExpanded"
      class="nama-expansion-item tw-w-full tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark"
      :class="[{'tw-rounded-lg tw-place-self-start':!isExpanded},block['namaClassTypeId']]"
      :id="blockId"
      @before-show="handleShow"
      @after-hide="handleHide"
      :dense="compactMode"
      :header-class="[isExpanded?'tw-w-full tw-bg-namaBackground-deep-light tw-pr-0 dark:tw-bg-namaBackground-deep-dark dark:tw-text-white tw-text-base tw-rounded-b-lg tw-shadow-[0_4px_4px_rgba(0,0,0,0.30)]':'tw-bg-namaBackground-dim-light tw-pr-0 dark:tw-bg-namaBackground-dim-dark dark:tw-text-white tw-text-base tw-rounded-lg']"
      expand-icon-class="dark:tw-text-white">
      <template #header>
        <div class="tw-flex tw-items-center tw-w-full tw-overflow-hidden tw-pr-3" :title="blockTitle">
          <div class="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-fit tw-w-fit">
            <NamaIcon
              :block="block"
              :presenter="presenter"
              :default-icon="blockDefinition?.defaultIconCode"
            class="tw-size-5 compact:tw-size-[18px] ultra:tw-size-[15px]"/>
            <div
              class="tw-inline-flex tw-items-center tw-gap-1 tw-flex-grow tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-line-clamp-1 tw-font-bold tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark">
              <span v-text="blockTitle"/>
              <div v-if="!!blockRef?.states?.length" class="tw-flex tw-ms-2 tw-items-center tw-gap-2 tw-overflow-x-auto">
                <div v-for="state in blockRef?.states">
                  <NamaIcon :constant="state.status"
                            v-slot="{ getNamaIconComponent, namaIconColor }">
                    <div :style="{ backgroundColor: namaIconColor }"
                         class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-1 tw-rounded tw-opacity-90 hover:tw-opacity-100 active-white">
                      <component :is="getNamaIconComponent()" class="!tw-size-[18px] tw-text-[18px]"/>
                      <span v-text="Translator.translateId(state.label)" class="tw-text-sm tw-whitespace-nowrap tw-text-white/90 tw-font-bold"/>
                    </div>
                  </NamaIcon>
                </div>
              </div>
              <EditUiElementPropertiesBtn :blockOrField="block" :entity-type="presenter.entityType"/>
            </div>
          </div>
          <div v-if="isExpanded && block['namaClassTypeId'] === 'EditScreenGrid' && blockRef?.isGridReady"
               class="tw-ml-2 tw-w- tw-max-w-[60%] tw-overflow-x-auto tw-flex-shrink-0">
            <div class="tw-whitespace-nowrap">
              <EditScreenGridToolbar ref="gridToolbarRef" :gridApi="blockRef?.gridApi" :handler="blockRef?.handler"/>
            </div>
          </div>
        </div>
      </template>
      <template #default>
        <q-separator class="tw-bg-transparent tw-size-4 compact:tw-size-2 ultra:tw-size-1.5"/>
        <q-card class="dark:tw-text-white tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-rounded-lg tw-relative"
                :data-ag-block-id="block.id"
                :class="{'tw-pb-4':isResizable()}">
          <NamaResizer @stopResize="onStopResize" :support-resize="isResizable()" :defaultHeight="calcBlockHeight()">
            <q-card-section class="tw-rounded-lg mobile:tw-p-2 compact:tw-p-1 ultra:tw-p-0.5 tw-pt-0 tw-pb-2 tw-h-full">
              <div :style="{maxHeight:isResizable() ? GUIUtilities.gridSizeToPixels(GridSize.XXLarge) + 'px' : '100%'}" class="tw-h-full">
                <component
                  v-if="blockDefinition"
                  :is="blockDefinition.component" :block="block"
                  :blockIndex="blockIndex" :presenter="presenter" :pageIndex="pageIndex" :collapsed="!isExpanded"
                  :showMainFile="showMainFile"
                  ref="blockRef" class="tw-h-full"
                  @navigateToNext="(args)=>emit('navigateToNext', args)" @discussionAdded="(args)=>emit('discussionAdded',args)"
                  @handleHide="()=>handleHide()" @onListScreenLayoutFetched="onListScreenLayoutFetched"
                  @toggleFindBar="gridToolbarRef?.toggleFindBar()"/>
                <div v-else>
                  <span v-text="'Unhandled block type: ' + block['namaClassTypeId']"/>
                </div>
              </div>
            </q-card-section>
          </NamaResizer>
        </q-card>
      </template>
    </q-expansion-item>
  </div>
</template>
